package no.vareflow.web

import no.vareflow.app.domain.*

// Shared building blocks, matching the phone's components (StatusBadge, EmptyCard, MetricCard …).

object Tone {
    const val GREEN = "green"     // #18774A
    const val AMBER = "amber"     // #9A6400
    const val GREY = "grey"       // #6F7788
    const val INDIGO = "indigo"   // #4E51E9
    const val RED = "red"         // #AD3D3D
    const val BROWN = "brown"     // #7A5A00
    const val BLUE = "blue"       // #2864A8
    const val ORANGE = "orange"   // #C25E00
}

fun El.statusBadge(text: String, tone: String) = span("badge tone-$tone", text)

fun El.emptyCard(text: String) = div("card empty", text)

fun El.pageHeader(title: String, subtitle: String? = null, actions: El.() -> Unit = {}) =
    div("page-header") {
        div("page-titles") {
            h1(title)
            if (subtitle != null) p(subtitle, "muted")
        }
        div("page-actions") { actions() }
    }

fun El.metricCard(label: String, value: String) = div("card metric") {
    div("metric-value", value)
    div("metric-label", label)
}

fun El.detailRow(label: String, value: String) = div("detail-row") {
    span("detail-label", label)
    span("detail-value", value)
}

/** Labeled text field for forms and dialogs. */
fun El.field(
    label: String,
    value: String,
    key: String,
    type: String = "text",
    placeholder: String = "",
    autocomplete: String? = null,
    hint: String? = null,
    onInput: (String) -> Unit
): El = el("label", "field") {
    span("field-label", label)
    input(value, key, "input", placeholder, type, onInput).also { input ->
        if (autocomplete != null) input.attr("autocomplete", autocomplete)
    }
    if (hint != null) span("field-hint", hint)
}

fun El.checkbox(label: String, checked: Boolean, key: String, onChange: (Boolean) -> Unit): El =
    el("label", "check") {
        el("input") {
            attr("type", "checkbox")
            attr("data-key", key)
            node.checked = checked
            on("change") { onChange(node.checked as Boolean) }
        }
        span("", label)
    }

fun El.chip(text: String, selected: Boolean, onClick: () -> Unit): El =
    button(text, "chip" + if (selected) " selected" else "", onClick = onClick).also {
        it.attr("aria-pressed", selected.toString())
    }

fun El.dialogActions(build: El.() -> Unit) = div("dialog-actions") { build() }

fun El.errorText(text: String?) {
    if (text != null) div("error-text", text).attr("role", "alert")
}

fun inventoryStatusTone(status: InventoryStatus) = when (status) {
    InventoryStatus.DRAFT -> Tone.BROWN
    InventoryStatus.ACTIVE -> Tone.GREEN
    InventoryStatus.CLOSED -> Tone.INDIGO
    InventoryStatus.ARCHIVED -> Tone.GREY
}

fun workStatusTone(status: CategoryWorkStatus) = when (status) {
    CategoryWorkStatus.NOT_STARTED -> Tone.GREY
    CategoryWorkStatus.IN_PROGRESS -> Tone.AMBER
    CategoryWorkStatus.DONE -> Tone.GREEN
}

/** Name dialog with Lagre/Avbryt and an optional Slett with confirmation (phone: ManageNameDialog). */
fun WebApp.nameDialog(
    title: String,
    initial: String,
    deleteTitle: String? = null,
    deleteMessage: String? = null,
    onDelete: (() -> Unit)? = null,
    onSave: (String) -> Boolean
) {
    val state = object { var name = initial; var error: String? = null; var confirmDelete = false }
    openDialog(title) { close ->
        if (state.confirmDelete && deleteTitle != null) {
            h2(deleteTitle, "dialog-subtitle")
            p(deleteMessage.orEmpty())
            dialogActions {
                button(t("Avbryt", "Cancel"), "btn text") { state.confirmDelete = false; requestRender() }
                button(t("Slett", "Delete"), "btn danger") { close(); onDelete?.invoke() }
            }
            return@openDialog
        }
        field(t("Navn", "Name"), state.name, "dlg-name") { state.name = it }
        errorText(state.error)
        dialogActions {
            if (onDelete != null) button(t("Slett", "Delete"), "btn text danger-text") { state.confirmDelete = true; requestRender() }
            span("spacer")
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Lagre", "Save"), "btn primary") {
                if (state.name.isBlank() || !onSave(state.name.trim())) {
                    state.error = t("Navnet er tomt eller brukes allerede.", "The name is empty or already in use.")
                    requestRender()
                } else close()
            }
        }
    }
}

/**
 * Vareflow's own dropdown instead of the browser's <select> (whose white system list does not
 * follow the theme): a button that opens a list in the page, styled like the rest of the app.
 * One list is open at a time ([WebApp.openSelect]); a click outside or Escape closes it.
 */
fun El.selectBox(
    app: WebApp,
    key: String,
    options: List<Pair<String, String>>,
    selected: String,
    ariaLabel: String,
    onChange: (String) -> Unit
): El = div("select-wrap") {
    val open = app.openSelect == key
    val current = options.firstOrNull { it.first == selected }?.second ?: options.firstOrNull()?.second.orEmpty()
    el("button", "input select-box" + if (open) " open" else "") {
        attr("type", "button")
        attr("data-key", key)
        attr("aria-haspopup", "listbox")
        attr("aria-expanded", open.toString())
        attr("aria-label", "$ariaLabel: $current")
        span("select-value", current)
        svg("""<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>""", "select-chevron")
        onClick {
            app.openSelect = if (open) null else key
            app.requestRender()
        }
        on("keydown") { e ->
            if (e.key == "Escape" && open) { e.preventDefault(); app.openSelect = null; app.requestRender() }
            if ((e.key == "ArrowDown" || e.key == "ArrowUp") && !open) { e.preventDefault(); app.openSelect = key; app.requestRender() }
        }
    }
    if (open) el("div", "select-list") {
        attr("role", "listbox")
        attr("aria-label", ariaLabel)
        options.forEach { (value, label) ->
            val isSelected = value == selected
            el("button", "select-option" + if (isSelected) " selected" else "") {
                attr("type", "button")
                attr("role", "option")
                attr("aria-selected", isSelected.toString())
                attr("data-key", "$key-opt-$value")
                span("", label)
                if (isSelected) span("select-check", "✓").attr("aria-hidden", "true")
                onClick {
                    app.openSelect = null
                    if (!isSelected) onChange(value)
                    app.requestRender()
                    // Back to the button for keyboard users.
                    setTimeout(0) { (document.querySelector("[data-key=\"$key\"]"))?.focus() }
                }
                on("keydown") { e ->
                    when (e.key) {
                        "Escape" -> { e.preventDefault(); app.openSelect = null; app.requestRender() }
                        "ArrowDown" -> { e.preventDefault(); node.nextElementSibling?.focus() }
                        "ArrowUp" -> { e.preventDefault(); node.previousElementSibling?.focus() }
                    }
                }
            }
        }
    }
}
